/**
 * UUID Version 7 Generator
 *
 * Generates time-ordered UUIDs according to RFC 9562.
 * Format: xxxxxxxx-xxxx-7xxx-yxxx-xxxxxxxxxxxx
 *
 * Structure:
 * - 48 bits: Unix timestamp in milliseconds
 * - 4 bits: Version (0111 = 7)
 * - 12 bits: Sub-millisecond precision or random data
 * - 2 bits: Variant (10)
 * - 62 bits: Random data
 *
 * @see https://www.rfc-editor.org/rfc/rfc9562.html#name-uuid-version-7
 */

/**
 * Generates a new UUID Version 7
 */
export function generateUUIDv7(): string {
  // Get current timestamp in milliseconds
  const timestamp = Date.now();

  // Generate random bytes for the rest of the UUID
  const randomBytes = new Uint8Array(10); // 10 bytes for random parts
  crypto.getRandomValues(randomBytes);

  // Convert timestamp to hex (48 bits = 12 hex chars)
  const timestampHex = timestamp.toString(16).padStart(12, '0');

  // Extract timestamp parts for UUID format (8-4 pattern)
  const timeLow = timestampHex.substring(0, 8);
  const timeMid = timestampHex.substring(8, 12);

  // Generate version and random bits (12 bits random)
  // Version 7 = 0111, so we use 0x7 as the first nibble
  const randomHigh = ((randomBytes[0] & 0x0f) | 0x70).toString(16).padStart(2, '0');
  const randomMid = randomBytes[1].toString(16).padStart(2, '0');
  const timeHiAndVersion = randomHigh + randomMid;

  // Generate variant and random bits (62 bits random)
  // Variant 10xx, so we set the top 2 bits to 10
  const clockSeqHi = ((randomBytes[2] & 0x3f) | 0x80).toString(16).padStart(2, '0');
  const clockSeqLow = randomBytes[3].toString(16).padStart(2, '0');
  const clockSeqAndVariant = clockSeqHi + clockSeqLow;

  // Generate remaining random bits (48 bits = 12 hex chars)
  const node = Array.from(randomBytes.slice(4, 10))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

  // Construct UUID in standard format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  return `${timeLow}-${timeMid}-${timeHiAndVersion}-${clockSeqAndVariant}-${node}`;
}
